import React from 'react';
import { Button, Dropdown, Tooltip, theme } from 'antd';
import { BgColorsOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/app/store/themeStore';
import { THEME_SCHEME_KEYS, getSchemePalette, type ThemeSchemeKey } from '@/app/theme/themeScheme';

type ThemeColorDropdownProps = {
  className?: string;
};

function ThemeColorDropdown({ className }: ThemeColorDropdownProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const activeScheme = useThemeStore((state) => state.activeScheme);
  const setActiveScheme = useThemeStore((state) => state.setActiveScheme);
  const [open, setOpen] = React.useState(false);

  const selectedColorName = t(`theme_scheme_${activeScheme}`);

  const panel = (
    <div
      style={{
        width: 260,
        padding: 12,
        borderRadius: 14,
        background: token.colorBgElevated,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxShadow: 'var(--app-shadow-soft)',
      }}
    >
      <div
        style={{
          marginBottom: 10,
          padding: '8px 10px',
          borderRadius: 10,
          background: 'var(--app-accent-soft-3)',
          border: `1px solid ${token.colorBorderSecondary}`,
          color: token.colorText,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {t('theme_selected_color')}: {selectedColorName}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        {THEME_SCHEME_KEYS.map((scheme) => (
          <Tooltip key={scheme} title={t(`theme_scheme_${scheme}`)}>
            <button
              type="button"
              onClick={() => {
                setActiveScheme(scheme as ThemeSchemeKey);
                setOpen(false);
              }}
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: activeScheme === scheme ? `2px solid rgba(255,255,255,0.95)` : `1px solid ${token.colorBorder}`,
                background: getSchemePalette(scheme).primary,
                boxShadow: activeScheme === scheme
                  ? `0 0 0 3px ${token.colorPrimaryBg}, 0 0 0 5px ${token.colorPrimaryBorder}, 0 8px 16px ${getSchemePalette(scheme).primary}55`
                  : 'none',
                cursor: 'pointer',
                outline: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: activeScheme === scheme ? 'translateY(-2px) scale(1.08)' : 'translateY(0) scale(1)',
                transition: 'transform 0.16s ease, box-shadow 0.16s ease',
              }}
            >
              {activeScheme === scheme && (
                <CheckOutlined
                  style={{
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 700,
                    textShadow: '0 1px 2px rgba(15, 23, 42, 0.3)',
                  }}
                />
              )}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      trigger={['click']}
      dropdownRender={() => panel}
    >
      <Tooltip title={t('theme_scheme_helper')}>
        <Button className={className} icon={<BgColorsOutlined />}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: getSchemePalette(activeScheme).primary,
                boxShadow: '0 0 0 2px var(--app-accent-soft-2)',
              }}
            />
          </span>
        </Button>
      </Tooltip>
    </Dropdown>
  );
}

export default ThemeColorDropdown;
